import React from 'react';
import { Box, Typography, Slider } from '@drone-js/core';
import { PriceFormatter } from '@agoda/react-handyman';
import { PriceFormatConfiguration } from '@agoda/react-handyman/lib/utils/priceFormatter/types';
import { ActivityPriceFormat } from 'gql/service/graph/activityService';
import { useSelector } from 'react-redux';
import { selectCmsFromBootstrap, selectCurrencyCodeFromBootstrap, selectLocaleFromBootstrap } from 'store/slice/bootstrap';
import { PriceFilterType, RangeFilterEvents } from 'core/type/Filter.types';
import { trackElementEvent } from 'core/messaging/analytics/analytics';
import { ActivitiesFilterPriceSliderThumbClickEventActivitiesFieldsV3 } from '@agoda/analytics-messages-definition-activities/events.split';
import { ActivitiesFilterPriceSliderThumbClickEventActivitiesManifestV3 } from '@agoda/analytics-messages-definition-activities/manifest/events';
import { differenceInMilliseconds } from 'date-fns';

export interface Props extends RangeFilterEvents {
    filter: PriceFilterType;
    isSidebar: boolean;
    isLoading: boolean;
}

const sliderDataComponent = {
    slider: 'activities-filter-price-slider',
    thumb: 'activities-filter-price-slider-thumb',
    track: 'activities-filter-price-slider-track',
};

const DETECT_CLICK_THRESHOLD = 200;

const PriceFilter: React.FC<Props> = React.memo((props) => {
    const { filter, onRangeFilterChange } = props;
    const [selectedMin, setSelectedMin] = React.useState<number>(() => 0);
    const [selectedMax, setSelectedMax] = React.useState<number>(() => props.filter?.selectedMax || 0);
    const [sliderValues, setSliderValues] = React.useState<number[]>([selectedMin, selectedMax]);
    const [isMouseDown, setIsMouseDown] = React.useState<Date | undefined>(undefined);
    const currencyCode = useSelector(selectCurrencyCodeFromBootstrap);
    const locale = useSelector(selectLocaleFromBootstrap);
    const priceFormatConfig: PriceFormatConfiguration = React.useMemo(() => ({ ...ActivityPriceFormat, currency: currencyCode, decimalPlaces: 0 }), [currencyCode]);
    const cms = useSelector(selectCmsFromBootstrap);

    React.useEffect(() => {
        setSelectedMin(filter.selectedMin);
        setSelectedMax(filter.selectedMax);
        setSliderValues([filter.selectedMin, filter.selectedMax]);
    }, [filter]);

    if (!filter.max) return null;

    const onFinalChange = (values: number[], final: boolean) => {
        const minValue = Math.min(values[0], values[1]);
        const maxValue = Math.max(values[0], values[1]);
        const calculatedMin = minValue;
        const calculatedMax = maxValue >= filter.max ? filter.max : maxValue;
        setSelectedMin(calculatedMin);
        setSelectedMax(calculatedMax);

        setSliderValues(values[0] < values[1] ? [calculatedMin, calculatedMax] : [calculatedMax, calculatedMin]);

        if (final && !props.isLoading) {
            setSliderValues([calculatedMin, calculatedMax]);
            onRangeFilterChange(filter.code, { value: calculatedMin, isInclusive: true }, { value: calculatedMax, isInclusive: true }, true);
            trackElementEvent<ActivitiesFilterPriceSliderThumbClickEventActivitiesFieldsV3>(ActivitiesFilterPriceSliderThumbClickEventActivitiesManifestV3.ids, {
                filter_min_value: values[0],
                filter_max_value: values[1],
                is_sidebar: props.isSidebar,
            });
        }
    };

    const handleMouseDown = () => {
        setIsMouseDown(new Date());
    };

    const handleMouseUp = () => {
        const mouseUpTimestamp = new Date();
        if (isMouseDown && !props.isLoading && differenceInMilliseconds(mouseUpTimestamp, isMouseDown) <= DETECT_CLICK_THRESHOLD) {
            onFinalChange(sliderValues, true);
        }
    };

    const formattedMinPrice = PriceFormatter.format(selectedMin, locale, priceFormatConfig);
    const formattedMaxPrice = PriceFormatter.format(selectedMax, locale, priceFormatConfig);

    // Position "relative" needed for 'activities-filter-price-slider' slider else they scroll down
    return (
        <Box data-testid="activities-filter-price-container" pb="24">
            <Typography data-testid="activities-filter-price-filter-title" mt="0" mb="16" variant="heading-default" role="heading" aria-level={2}>
                {filter.label}
            </Typography>
            <Box display="flex" justifyContent="space-between" mb="12">
                <Typography data-testid="activities-filter-price-min" variant="body-s">
                    {formattedMinPrice}
                </Typography>
                <Typography data-testid="activities-filter-price-max" variant="body-s">
                    {formattedMaxPrice}
                </Typography>
            </Box>
            {/* eslint-disable-next-line @agoda/agoda-a11y/no-incorrect-drone-box-usage -- @agoda/agoda-a11y/no-incorrect-drone-box-usage[457c6254-1c46-4785-8ff9-d7411f264d9e] */}
            <Box position="relative" data-testid="activities-filter-price-slider" onMouseDown={handleMouseDown} onMouseUp={handleMouseUp}>
                <Slider
                    getAriaLabel={(index) => (index === 0 ? cms[331604] : cms[331605])}
                    getAriaValueText={(value) => `$${value}`}
                    data-element-name={sliderDataComponent}
                    min={0}
                    max={filter.max}
                    values={sliderValues}
                    onChange={onFinalChange}
                />
            </Box>
        </Box>
    );
});
PriceFilter.displayName = 'PriceFilter';

export default PriceFilter;
